import {
    createContext,
    forwardRef,
    useContext,
    useId,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { FocusOn } from 'react-focus-on'
import { useTranslation } from 'react-i18next'
import CollaborationCursor from '@tiptap/extension-collaboration-cursor'
import History from '@tiptap/extension-history'
import { EditorContent, useEditor } from '@tiptap/react'
import { hasJsonStructure } from 'helpers'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import FieldLabel from 'js/components/FieldLabel'
import HideVisually from 'js/components/HideVisually/HideVisually'
import TextElement from 'js/components/Text/Text'
import { useTipTapEditorStore } from 'js/lib/stores'
import globalStateContext from 'js/lib/withGlobalState/globalStateContext'

import WarnIcon from 'icons/warn.svg'

import BubbleMenu from './BubbleMenu/BubbleMenu'
import { CollaborationExtension as Collaboration } from './CollaborationExtension'
import AssistButtons from './components/Buttons'
import KeyboardShortcutsButton from './components/KeyboardShortcutsButton'
import getExtensions from './getExtensions'
import StyledTiptapEditor from './StyledTiptapEditor'
import Toolbar from './Toolbar/Toolbar'

export const TiptapEditorContext = createContext(null)

const StyledTextElement = styled(TextElement)`
    &.abstract-helper {
        svg {
            display: none !important;
        }
        &.Chars__Remaining--Warning {
            color: ${(p) => p.theme.color.warn.main};

            svg {
                display: inline-block !important;
            }
        }
    }
`

const TiptapEditor = forwardRef(
    (
        {
            autofocus = false,
            borderStyle = 'rounded',
            className = '',
            collab,
            content,
            contentType = 'json',
            disableNewLine = false,
            disableNewParagraph = false,
            getEditorInstance,
            group,
            hasError = false,
            height = 'normal',
            'aria-label': ariaLabel = '',
            label = '',
            placeholder = '',
            maxChars,
            name,
            onBlur,
            onChange,
            options = {
                textAnchor: false,
                textCSSClass: false,
                textLanguage: false,
                textFormat: false,
                textStyle: false,
                textLink: false,
                textQuote: false,
                textList: false,
                insertMedia: false,
                disableAlignImage: false,
                insertAccordion: false,
                insertButton: false,
                insertTable: false,
                insertMention: false,
            },
            required,
            showPaddingBottom,
            hideKeyboardShortcuts = false,
            textSize = 'normal',
            delayMentionSuggestions,
            isFullScreenEnabled,
            ...rest
        },
        ref,
    ) => {
        const {
            textFormat,
            textStyle,
            textLink,
            textAnchor,
            textCSSClass,
            textLanguage,
            textQuote,
            textList,
            insertAccordion,
            insertButton,
            insertMedia,
            disableAlignImage,
            insertTable,
            insertMention,
        } = options

        // Use a unique ID for each TiptapEditor instance
        // This reference is used to identify the TiptapEditor instance
        // in the TipTapEditorStore
        const reference = useId()

        const refWrapper = useRef()
        const refToolbar = useRef()

        const isFullScreen = useTipTapEditorStore((state) =>
            state.isFullScreenByReference(reference),
        )

        const { globalState } = useContext(globalStateContext)
        const { allowTextCssClasses } = globalState

        const editor = useEditor({
            editable: true,
            editorProps: {
                attributes: {
                    'aria-label': ariaLabel,
                    'aria-describedby': `${name}-describe`,
                    'aria-multiline': true,
                    'aria-required': !!required,
                    role: 'textbox',
                },
            },
            extensions: [
                ...getExtensions({
                    placeholder,
                    maxChars,
                    textStyle,
                    textLink,
                    textAnchor,
                    textCSSClass,
                    textLanguage,
                    textQuote,
                    textList,
                    insertButton,
                    insertMedia,
                    insertAccordion,
                    insertTable,
                    insertMention,
                    disableNewLine,
                    disableNewParagraph,
                    delayMentionSuggestions,
                    group,
                }),
                ...(collab?.provider && collab?.user
                    ? [
                          Collaboration.configure({
                              document: collab.provider.document,
                              field: 'default',
                              user: {
                                  guid: collab.user.guid,
                                  name: collab.user.name,
                              },
                          }),
                          CollaborationCursor.configure({
                              provider: collab.provider,
                              user: {
                                  guid: collab.user.guid,
                                  name: collab.user.name,
                              },
                          }),
                      ]
                    : [History]),
            ],
            autofocus: autofocus ? 'end' : false,
            injectCSS: false,
            content: collab?.provider
                ? undefined
                : contentType === 'json'
                  ? hasJsonStructure(content)
                      ? JSON.parse(content)
                      : content
                  : content,
            onUpdate({ editor }) {
                if (onChange) {
                    onChange(getValue(editor))
                }
            },
            onFocus({ editor }) {
                handleEditorKeyDownWrapper.current = (evt) =>
                    handleEditorKeyDown(evt, editor)

                window.addEventListener(
                    'keydown',
                    handleEditorKeyDownWrapper.current,
                )
            },
            onBlur({ editor, event }) {
                window.removeEventListener(
                    'keydown',
                    handleEditorKeyDownWrapper.current,
                )

                if (onBlur) {
                    onBlur(event, getValue(editor))
                }
            },
            onCreate({ editor }) {
                getEditorInstance && getEditorInstance(editor)
            },
        })

        const keyboardShortcutsButtonRef = useRef(null)

        const [
            keyboardShortcutsModalIsVisible,
            setKeyboardShortcutsModalIsVisible,
        ] = useState(false)

        // Store the event listener reference in a ref
        // This way we can remove the event listener when the editor is destroyed
        // Without the ref, the event listener would not be removed correctly
        const handleEditorKeyDownWrapper = useRef()

        const handleEditorKeyDown = (evt, editor) => {
            if (evt.altKey && evt.key === 'F10') {
                const toolbar = refToolbar?.current
                if (toolbar) {
                    evt.preventDefault()
                    toolbar.setAttribute('tabindex', 0)
                    toolbar.focus()
                }
            }

            if (evt.key === 'Escape') {
                const { doc, selection } = editor.view.state
                const { from, to } = selection
                const hasTextSelection = doc.textBetween(from, to).length > 0
                if (hasTextSelection) {
                    editor.commands.setTextSelection({ from: to, to })
                }
            }

            if ((evt.metaKey || evt.ctrlKey) && evt.key === '/') {
                evt.preventDefault()
                keyboardShortcutsButtonRef.current?.click()
            }
        }

        const getValue = (editor) => {
            if (!editor) {
                console.error('Please pass editor object')
                return null
            }
            if (contentType === 'html') {
                return editor.isEmpty ? '' : editor.getHTML()
            } else if (contentType === 'json') {
                return editor.isEmpty ? '' : JSON.stringify(editor.getJSON())
            } else {
                console.error('Content type does not exist')
            }
        }

        useImperativeHandle(ref, () => ({
            getValue() {
                return getValue(editor)
            },
            clearContent() {
                editor.commands.setContent(null)
            },
        }))

        const { t } = useTranslation()

        const getRemainingCharacterWarning = (maxChars, numberOfCharacters) => {
            const limit = 25
            if (
                maxChars - numberOfCharacters > 0 &&
                maxChars - numberOfCharacters <= limit
            ) {
                return {
                    message: t('editor.input-max-length-notify'),
                }
            } else if (maxChars === numberOfCharacters) {
                return {
                    message: t('editor.input-max-length-reached'),
                    className: 'Chars__Remaining--Warning',
                }
            }
            return {}
        }

        const toolbarPlugins = {
            textFormat,
            textQuote,
            textList,
            insertButton,
            insertMedia,
            insertAccordion,
            insertTable,
        }

        const toolbarIsVisible =
            textFormat ||
            textQuote ||
            textList ||
            insertButton ||
            insertMedia ||
            insertAccordion ||
            insertTable

        const bubbleMenuPlugins = {
            textStyle,
            textLink,
            textAnchor,
            textCSSClass: allowTextCssClasses && textCSSClass,
            textLanguage,
        }

        const labelIsActive = label && editor?.isEmpty === false

        const assistButtons = [
            hideKeyboardShortcuts !== true && (
                <KeyboardShortcutsButton
                    key="keyboard-shortcuts-button"
                    ref={keyboardShortcutsButtonRef}
                    modalIsVisible={keyboardShortcutsModalIsVisible}
                    setModalIsVisible={setKeyboardShortcutsModalIsVisible}
                    options={options}
                    toolbarIsVisible={toolbarIsVisible}
                />
            ),
        ].filter(Boolean)

        return (
            <TiptapEditorContext.Provider
                value={{
                    groupGuid: group?.guid,
                    disableAlignImage,
                }}
            >
                <StyledTiptapEditor
                    ref={refWrapper}
                    $borderStyle={isFullScreen ? 'none' : borderStyle}
                    $height={height}
                    $showPaddingBottom={showPaddingBottom}
                    $textSize={isFullScreen ? 'large' : textSize}
                    $hasError={hasError}
                    $isCollab={!!collab?.provider}
                    $labelIsActive={labelIsActive}
                    $allowTextCssClasses={allowTextCssClasses}
                    className={className}
                    $isFullScreen={isFullScreen}
                    {...rest}
                >
                    <FocusOn enabled={isFullScreen}>
                        <Toolbar
                            ref={refToolbar}
                            editor={editor}
                            containerElement={refWrapper}
                            contentGuid={collab?.guid}
                            reference={reference}
                            isVisible={toolbarIsVisible}
                            isFullScreenEnabled={isFullScreenEnabled}
                            {...toolbarPlugins}
                        />
                        <BubbleMenu editor={editor} {...bubbleMenuPlugins} />
                        <div className="TiptapEditorContentWrapper">
                            <FieldLabel
                                placeholder={placeholder}
                                label={label}
                                active={labelIsActive}
                                hasError={hasError}
                                className="TiptapEditorLabel"
                            />
                            <EditorContent
                                editor={editor}
                                className={`TiptapEditorContent`}
                            />
                        </div>
                        {/* Keep KeyboardShorcutsButton outside .TiptapEditorContentWrapper, otherwise it interferes with BubbleMenu's keyboard accessibility */}
                        {assistButtons.length > 0 && (
                            <AssistButtons $isVisible={editor?.isFocused}>
                                {assistButtons}
                            </AssistButtons>
                        )}
                    </FocusOn>
                </StyledTiptapEditor>
                <HideVisually id={`${name}-describe`} aria-hidden>
                    {t('editor.focus-toolbar-helper')}
                </HideVisually>
                {maxChars && (
                    <>
                        <HideVisually role="alert" aria-live="assertive">
                            {
                                getRemainingCharacterWarning(
                                    maxChars,
                                    editor?.view.state.doc.textContent?.length,
                                ).message
                            }
                        </HideVisually>
                        <StyledTextElement
                            id={`${name}-helper`}
                            size="small"
                            variant="grey"
                            className={`abstract-helper ${
                                getRemainingCharacterWarning(
                                    maxChars,
                                    editor?.view.state.doc.textContent?.length,
                                ).className
                            }`}
                            style={{ marginTop: '8px' }}
                        >
                            <WarnIcon
                                style={{
                                    display: 'inline-block',
                                    verticalAlign: 'text-top',
                                    marginRight: '5px',
                                }}
                            />
                            {`${
                                editor?.view.state.doc.textContent?.length || 0
                            } / ${maxChars}`}
                        </StyledTextElement>
                    </>
                )}
            </TiptapEditorContext.Provider>
        )
    },
)

TiptapEditor.displayName = 'TiptapEditor'

TiptapEditor.propTypes = {
    contentType: PropTypes.oneOf(['json', 'html']),
    borderStyle: PropTypes.oneOf(['none', 'rounded']),
    height: PropTypes.oneOf(['small', 'normal', 'large']),
    textSize: PropTypes.oneOf(['small', 'normal', 'large']),
    maxChars: PropTypes.number,
    options: PropTypes.shape({
        textFormat: PropTypes.bool,
        textStyle: PropTypes.bool,
        textLink: PropTypes.bool,
        textAnchor: PropTypes.bool,
        textCSSClass: PropTypes.bool,
        textLanguage: PropTypes.bool,
        textQuote: PropTypes.bool,
        textList: PropTypes.bool,
        insertAccordion: PropTypes.bool,
        insertMedia: PropTypes.bool,
        disableAlignImage: PropTypes.bool,
        insertTable: PropTypes.bool,
        insertButton: PropTypes.bool,
        insertMention: PropTypes.bool,
    }),
    group: PropTypes.shape({
        guid: PropTypes.string,
        isClosed: PropTypes.bool,
    }),
    autofocus: PropTypes.bool,
    hasError: PropTypes.bool,
    disableNewLine: PropTypes.bool,
    disableNewParagraph: PropTypes.bool,
    delayMentionSuggestions: PropTypes.number,
}

export default TiptapEditor
